/**
 * Performance Tab Component - Real supplier performance metrics
 */

import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { ShieldCheck, Truck, Clock, Info } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import { SupplierProfile } from '../../lib/api';
import { useQualityMeasureLabel } from '../../lib/qualityLabels';

interface PerformanceTabProps {
  supplier: SupplierProfile;
}

function formatNumber(value: number | string | undefined | null, decimals = 0): string {
  if (value === undefined || value === null) return '-';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '-';
  return num.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

function StatRow({ label, value, suffix }: { label: string; value: string; suffix?: string }) {
  return (
    <div className="flex items-center justify-between py-1.5">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-sm font-semibold">
        {value}{suffix || ''}
      </span>
    </div>
  );
}

export function PerformanceTab({ supplier }: PerformanceTabProps) {
  const otifPercent = supplier.otif_score_percentage ?? 0;
  const otdPercent = supplier.on_time_delivery_rate ?? 0;
  const totalPpm = supplier.total_ppm ?? 0;
  const qualityLabel = useQualityMeasureLabel();

  return (
    <div className="space-y-4">
      {/* Top-level KPI cards */}
      <div className="grid gap-4 md:grid-cols-4">
        {/* OTIF Score */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-1.5">
              <Truck className="h-4 w-4 text-muted-foreground" />
              OTIF Score
              {/* CR-5557: surface the in-codebase OTIF rule so users
                  can interpret the number. Final OTIF semantics
                  (time + qty + tolerances) are pending product sign-off. */}
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="h-3.5 w-3.5 text-muted-foreground/70 cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs text-xs">
                    Counts PO lines with no late GRNs (early & on-time
                    receipts both pass). The "in-full" quantity check
                    is not yet enforced — final OTIF semantics under
                    review.
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatNumber(otifPercent, 1)}%
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {formatNumber(supplier.otif_count)} of {formatNumber(supplier.po_line_count)} PO lines
            </p>
          </CardContent>
        </Card>

        {/* OTD Rate */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-1.5">
              <Clock className="h-4 w-4 text-muted-foreground" />
              OTD Rate
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatNumber(otdPercent, 1)}%
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {/* CR-5557: numerator is on-time + early (= passed ODD), so
                  the percentage above and this subtitle agree. */}
              {formatNumber((supplier.ontime_count ?? 0) + (supplier.early_count ?? 0))} on-time of {formatNumber(supplier.grn_count)} GRNs
              {supplier.otd_timeframe_label && (
                <span className="ml-1">· {supplier.otd_timeframe_label}</span>
              )}
            </p>
          </CardContent>
        </Card>

        {/* Total PPM */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-1.5">
              <ShieldCheck className="h-4 w-4 text-muted-foreground" />
              Total {qualityLabel}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatNumber(totalPpm, 0)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {formatNumber(supplier.reject_qty)} rejects / {formatNumber(supplier.qty_received)} received
            </p>
          </CardContent>
        </Card>

      </div>

      {/* Detailed breakdown cards */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* Quality Metrics */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Quality Metrics</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="divide-y divide-border">
              <StatRow
                label={`Total ${qualityLabel}`}
                value={formatNumber(supplier.total_ppm, 0)}
              />
              <StatRow
                label={`Supplier Liability ${qualityLabel}`}
                value={formatNumber(supplier.supplier_liability_ppm, 0)}
              />
              <StatRow
                label={`Customer Liability ${qualityLabel}`}
                value={formatNumber(supplier.customer_liability_ppm, 0)}
              />
              <StatRow
                label="NCR Count"
                value={formatNumber(supplier.ncr_count)}
              />
              <StatRow
                label="Qty Received"
                value={formatNumber(supplier.qty_received)}
              />
              <StatRow
                label="Reject Qty"
                value={formatNumber(supplier.reject_qty)}
              />
              <StatRow
                label={`${qualityLabel} Mapped Score`}
                value={supplier.ppm_mapped_score !== null && supplier.ppm_mapped_score !== undefined ? formatNumber(supplier.ppm_mapped_score, 1) : '-'}
              />
            </div>
          </CardContent>
        </Card>

        {/* Delivery Metrics - OTIF */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">OTIF Delivery (PO Lines)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="divide-y divide-border">
              <StatRow
                label="OTIF Score"
                value={`${formatNumber(otifPercent, 1)}%`}
              />
              <StatRow
                label="OTIF Lines"
                value={formatNumber(supplier.otif_count)}
              />
              <StatRow
                label="Total PO Lines"
                value={formatNumber(supplier.po_line_count)}
              />
              <StatRow
                label="Lines in Arrears"
                value={formatNumber(supplier.arrears_count)}
              />
              <StatRow
                label="Qty Difference"
                value={formatNumber(supplier.qty_diff)}
              />
              <StatRow
                label="OTIF Mapped Score"
                value={supplier.otif_mapped_score !== null && supplier.otif_mapped_score !== undefined ? formatNumber(supplier.otif_mapped_score, 1) : '-'}
              />
            </div>
          </CardContent>
        </Card>

        {/* Delivery Metrics - OTD (GRN based) */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">OTD Delivery (GRN)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="divide-y divide-border">
              <StatRow
                label="OTD Rate"
                value={`${formatNumber(otdPercent, 1)}%`}
              />
              <StatRow
                label="Total GRNs"
                value={formatNumber(supplier.grn_count)}
              />
              <StatRow
                label="On-Time"
                value={formatNumber(supplier.ontime_count)}
              />
              <StatRow
                label="Late"
                value={formatNumber(supplier.late_count)}
              />
              <StatRow
                label="Early"
                value={formatNumber(supplier.early_count)}
              />
            </div>
          </CardContent>
        </Card>

        {/* Cost & Order Statistics */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Cost & Orders</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="divide-y divide-border">
              <StatRow
                label="Total Orders"
                value={formatNumber(supplier.total_orders)}
              />
              <StatRow
                label="Completed Orders"
                value={formatNumber(supplier.completed_orders)}
              />
              {supplier.total_value && (
                <StatRow
                  label={`Total Value${supplier.currency ? ` (${supplier.currency})` : ''}`}
                  value={formatNumber(supplier.total_value, 2)}
                />
              )}
              {supplier.reject_value && (
                <StatRow
                  label="Reject Value"
                  value={formatNumber(supplier.reject_value, 2)}
                />
              )}
              <StatRow
                label="Total Cost of Quality"
                value={supplier.total_cost_of_quality !== undefined ? `${formatNumber(supplier.total_cost_of_quality, 2)}%` : '-'}
              />
              {supplier.carbon_emission !== undefined && supplier.carbon_emission !== null && (
                <StatRow
                  label="CO₂ Emissions"
                  value={`${formatNumber(supplier.carbon_emission, 2)} kg`}
                />
              )}
            </div>
          </CardContent>
        </Card>
      </div>

    </div>
  );
}
